import React, { useState, useEffect, useRef, useContext } from 'react'
import styled, { keyframes, css } from 'styled-components'
import loadable from '@loadable/component'
import { SiteLink } from '../shared/SiteLink'
import { useTrustPilot } from '../../hooks/useTrustPilot'
import ReviewStarsStore from '../../components/shared/ReviewStarsStore'
import { useAnnouncementClickedTracking } from '../../hooks/tracking'
import { LayoutContext, useUI } from '../../contexts'
import { getAvailable } from '../../utils'

const TIMER_INTERVAL = 4500

const AnnouncementV2 = ({ announcement }) => {
  const { node_locale, currency } = useContext(LayoutContext)
  const containerEl = useRef()
  const trustPilot = useTrustPilot()
  // + 1 for review element
  let announcementLength = announcement.filter(getAvailable).length + 1
  if (node_locale === 'de') announcementLength += 1 // + 1 for the trustpilot review element
  let currentIndex = -1
  let nextIndex

  const useInterval = () => {
    if (currentIndex >= announcementLength - 1) {
      currentIndex = 0
    } else {
      currentIndex += 1
    }
    nextIndex = currentIndex + 1
    if (nextIndex >= announcementLength) {
      nextIndex = 0
    }
    containerEl.current.children[nextIndex].classList.add('active')
    containerEl.current.children[currentIndex].classList.remove('active')
    // for first element only; start with element already at the center to prevent layout shift
    containerEl.current.children[currentIndex].classList.remove('first')
  }

  const SingleItem = ({ text, svg }) => (
    <>
      {svg && (
        <SVGWrapper dangerouslySetInnerHTML={{ __html: svg?.svg?.content }} />
      )}
      <p>{text}</p>
    </>
  )

  const announcementItems = announcement
    .filter(getAvailable)
    .map(({ text, link, svg }, index) =>
      link ? (
        <AnnouncementDiv key={index}>
          <Link
            to={link.target && link.target.slug}
            extension={link.linkExtension}
            onClick={() => {
              useAnnouncementClickedTracking({
                currency: currency?.code,
                locale: node_locale,
                label: text,
                position: index + 2,
                destination: `/${node_locale}/${link.target.slug}/`,
              })
            }}
            state={{
              referrer:
                typeof window !== 'undefined' && window.location.pathname,
            }}
          >
            <SingleItem text={text} svg={svg} />
          </Link>
        </AnnouncementDiv>
      ) : (
        <AnnouncementDiv key={index}>
          <NoLink>
            <SingleItem text={text} svg={svg} />
          </NoLink>
        </AnnouncementDiv>
      ),
    )

  useEffect(() => {
    const interval = setInterval(() => {
      useInterval()
    }, TIMER_INTERVAL)

    return () => {
      clearInterval(interval)
    }
  }, [])

  return (
    <Container ref={containerEl}>
      <AnnouncementDiv
        reviews
        className='first'
        onClick={() => {
          useAnnouncementClickedTracking({
            currency: currency?.code,
            locale: node_locale,
            label: 'Trustpilot Reviews',
            position: 1,
            destination: `Review Page`,
          })
        }}
        state={{
          referrer: typeof window !== 'undefined' && window.location.pathname,
        }}
      >
        <ReviewStarsStore
          trustpilotStars={trustPilot.score.stars}
          totalNumberOfReviews={trustPilot.total}
        />
      </AnnouncementDiv>
      {node_locale === 'de' && (
        <AnnouncementDiv
          reviews
          onClick={() => {
            useAnnouncementClickedTracking({
              currency: currency?.code,
              locale: node_locale,
              label: 'Trustpilot Reviews',
              position: 1,
              destination: `Review Page`,
            })
          }}
          state={{
            referrer: typeof window !== 'undefined' && window.location.pathname,
          }}
        >
          <ReviewStarsStore
            trustpilotStars={trustPilot.score.stars}
            totalNumberOfReviews={trustPilot.total}
            showTrustpilot
          />
        </AnnouncementDiv>
      )}
      {announcementItems}
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  background-color: #f1f1f1;
  overflow: hidden;
  height: ${(props) => props.theme.announcementHeight.mobile};
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: ${(props) => props.theme.announcementHeight.desktop};
  }

  p {
    font-size: ${(props) => props.theme.fontSize.m23};
    padding: ${(props) => props.theme.space.marginMobile / 4}vw 0;
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d12};
      padding: ${(props) => props.theme.space.marginDesktop / 3}vw 0;
    }
  }
`

const flyBy = keyframes`
    0% {
      transform: translateX(100%);
    }
    10% {
      transform: translateX(0);
    }
    90% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(-100%);
    }
`

const flyAway = keyframes`
  0% {
    transform: translateX(0);
  }
  90% {
      transform: translateX(0);
    }
  100% {
    transform: translateX(-100%);
  }
`

const AnnouncementDiv = styled.div`
  display: inline-flex;
  position: absolute;
  background-color: #f1f1f1;
  justify-content: center;
  align-items: center;
  transform: translateX(100%);
  will-change: transform;
  height: ${(props) => props.theme.announcementHeight.mobile};
  width: 100%;

  &.active {
    animation: ${flyBy} ${TIMER_INTERVAL / 1000}s
      ${(props) => props.theme.easeInOut.quart};
  }

  &.first {
    animation: ${flyAway} ${TIMER_INTERVAL / 1000}s
      ${(props) => props.theme.easeInOut.quart};
  }

  /* for preventing layout shift */
  ${(props) =>
    props.reviews &&
    `
    & > div {
      width: 100%;
      height: 100%;
    }
    a {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `}

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    height: ${(props) => props.theme.announcementHeight.desktop};
  }
`

const Link = styled(SiteLink)`
  display: flex;
  align-items: center;
  margin: 0;
  transition: color 0.2s;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: 0 1.5vw;
    &:hover {
      color: ${(props) => props.theme.colors.blue};
      transition: color 0.2s;
      svg {
        fill: ${(props) => props.theme.colors.blue};
      }
      path {
        transition: stroke 0.3s;
        /* stroke: ${(props) => props.theme.colors.blue}; */
      }
    }
  }
`

const NoLink = styled.div`
  display: flex;
  align-items: center;
  margin: 0 4vw;
  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    margin: 0 1.5vw;
  }
`

const SVGWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    width: auto;
    height: 5vw;
    margin-right: 1.3vw;
    transition: fill 0.2s;
    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      height: 1.1vw;
      margin-right: 0.4vw;
    }
    path {
      /* stroke: currentColor; */
      transition: stroke 0.2s;
    }
  }
`

export default AnnouncementV2
